import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import SelectField from '../../../components/ui/SelectField';
import BookingCustomerClassSelect from './components/BookingCustomerClassSelect';
import InputField from '../../../components/ui/InputField';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import { usePriceAdjustmentAudit } from '../../../hooks/usePriceAdjustmentAudit';
import DateRangeField from '../../../components/ui/DateRangeField';
import AdditionalServices from './components/AdditionalServices/AdditionalServices';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDispatch } from 'react-redux';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { usePricePreview } from '../../../hooks/usePricePreview';
import {
    AdditionalService,
    clearBookingCreationStates,
    clearCheckOutDate,
    setAddress,
    setApartments,
    setCheckInDate,
    setCheckOutDate,
    setCity,
    setComment,
    setCompanyName,
    setCountry,
    setEmail,
    setEmailNote,
    setFirstName,
    setForSomeoneElse,
    setLastName,
    setNumberOfGuests,
    setPersoCity,
    setPhone,
    setSelectedApartment,
    setSelectedApartmentDetails,
    setSomeoneElseEmail,
    setSomeoneElseFirstName,
    setSomeoneElseLastName,
    setSomeoneElsePhone,
    setZipCode,
} from '../../../store/slices/bookingCreationSlice';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { stationDateTime } from '../../../utils/stationTime';
import { useCityTimeZone, useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import { usePickedApartmentAvailability } from '../../../hooks/usePickedApartmentAvailability';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useGuestCapacityGuard } from '../../../hooks/useGuestCapacityGuard';
import { useFetchApartments } from '../../../flows/fetchApartmentsFlow';
import { useFetchApartmentDetails } from '../../../flows/fetchApartmentDetailsFlow';
import { useCountryOptions } from '../../../hooks/useCountryOptions';
import { useCreateRoomRentalBooking } from '../../../flows/createRoomRentalBookingFlow';
import { useTranslation } from 'react-i18next';
import SnackbarComponent from '../../common/SnackbarComponent';
import {
    CreateRoomRentalBookingPayload,
    PersonalDataValidation,
} from '../../../types/bookingCreation';
import { useNavigate } from 'react-router';
import { paths } from '../../../routes';
import FilteredSelect from '../../common/FilteredSelect';
import { useFetchBooking } from '../../../flows/fetchBookingsFlow';
import { AdditionalService as AdditionalServiceType } from '../../../types/bookingCreation';
import CustomButton from '../../../components/CustomButton';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { customerMailRefusalOf, savedOutcomeToast } from '../../../utils/customerMailGuard';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { applyContactPrefill } from '../../../utils/organizationPrefill';
import { attributionPayload } from '../../../utils/costCenter';
import { useAttributedClassId } from '../../../hooks/useAttributedClassId';
import { BOOKING_EMAIL_NOTE_MAX_LENGTH, extractAddress, isCreateBookingValid, sortByLocaleName } from '../../../utils';
import ReservationSummary from '../../../components/ReservationSummary';
import CustomModal from '../../common/CustomModal';
import i18n from 'i18next';


dayjs.extend(isSameOrBefore);

const CreateRoomBookingPage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        options: countryOptions,
        toLabel: toCountryLabel,
        toValue: toCountryValue,
    } = useCountryOptions();
    const navigate = useNavigate();
    const [cities, setCities] = useState<string[]>([]);
    const [apartmentNames, setApartmentNames] = useState<string[]>([]);
    const [loadingCities, setLoadingCities] = useState(true);
    const [loadingApartments, setLoadingApartments] = useState(false);
    const [open, setOpen] = useState(false);
    const [, setLoadingCountries] = useState(true);
    const [discount, setDiscount] = useState<number>(0);
    // #1942: a price adjustment made here is stored on the reservation with the
    // same audit trail an edit writes — reason + acting admin.
    const {
        reason: priceAdjustmentReason,
        setReason: setPriceAdjustmentReason,
        payload: priceAdjustmentAudit,
    } = usePriceAdjustmentAudit(discount);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });
    const [loadingCreateBookingOnlySave, setLoadingCreateBookingOnlySave] = useState(false);
    const [loadingCreateBookingSaveAndSend, setLoadingCreateBookingSaveAndSend] = useState(false);
    const [bookingFor, setBookingFor] = useState('customer');
    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const useCases = config?.services;

    const createRoomRentalBooking = useCreateRoomRentalBooking();
    // #1808: the per-booking class override (apartment side — corporate rate,
    // returning guest). Same component and same semantics as the venue page.
    const [customerClassId, setCustomerClassId] = useState<string | null>(null);
    // #1810: the organisation (unit) the booking is made for, and the invoice
    // recipient's cost centre.
    const [attribution, setAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    // Bumped when a save starts the next booking: the field remounts, so it
    // also forgets what it filled and whether a cost centre was typed.
    const [attributionFormKey, setAttributionFormKey] = useState(0);
    const inheritedClassId = useAttributedClassId(attribution);
    const fetchApartmentDetails = useFetchApartmentDetails();
    const fetchBooking = useFetchBooking(useCases ?? ['']);

    const onFirstNameChange = (firstName: string) =>
        dispatch(setFirstName({ firstName }));
    const onLastNameChange = (lastName: string) =>
        dispatch(setLastName({ lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setPhone({ phone: phone }));
    };
    const onCompanyNameChange = (company: string) => {
        dispatch(setCompanyName({ company: company }));
    };
    const onCommentChange = (comment: string) => {
        dispatch(setComment({ comment: comment }));
    };
    const onEmailNoteChange = (emailNote: string) => {
        dispatch(setEmailNote({ emailNote: emailNote }));
    };
    const onEmailChange = (email: string) => dispatch(setEmail({ email }));

    const onAddressChange = (address: string) =>
        dispatch(setAddress({ address }));
    const onCityChange = (city: string) => dispatch(setPersoCity({ city }));
    const onCountryChange = (country: string) => {
        dispatch(setCountry({ country: country }));
    };
    const onZipCodeChange = (zipCode: string) => {
        dispatch(setZipCode({ zipCode }));
    };

    const onSomeoneElseFirstNameChange = (firstName: string) => {
        dispatch(setSomeoneElseFirstName({ firstName: firstName }));
    };
    const onSomeoneElseLastNameChange = (lastName: string) => {
        dispatch(setSomeoneElseLastName({ lastName: lastName }));
    };
    const onSomeoneElseEmailChange = (email: string) => {
        dispatch(setSomeoneElseEmail({ email: email }));
    };
    const onSomeoneElsePhoneChange = (phone: string) => {
        dispatch(setSomeoneElsePhone({ phone: phone }));
    };

    const personalDetails = useSelector(
        (state: RootState) => state.bookingCreation.personalData,
    );

    const someoneElseDetails = useSelector(
        (state: RootState) => state.bookingCreation.someoneElseData,
    );

    const orderData = useSelector(
        (state: RootState) => state.bookingCreation.orderData,
    );

    const isForSomeoneElse = useSelector(
        (state: RootState) => state.bookingCreation.isForSomeoneElse,
    );

    const city = useSelector(
        (state: RootState) => state.bookingCreation.orderData.city,
    );
    const startDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkInDate,
    );
    const endDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkOutDate,
    );
    const numberOfGuests = useSelector(
        (state: RootState) => state.bookingCreation.orderData.numberOfGuests,
    );
    const comment = useSelector(
        (state: RootState) => state.bookingCreation.orderData.comment,
    );
    const emailNote = useSelector(
        (state: RootState) => state.bookingCreation.orderData.emailNote,
    );

    const apartments = useSelector(
        (state: RootState) => state.bookingCreation.apartments,
    );

    const selectedApartment = useSelector(
        (state: RootState) => state.bookingCreation.selectedApartment,
    );
    // #3219: the stay is booked on the clock of the apartment's location.
    const timeZone = useLocationTimeZone(selectedApartment?.locationId);

    const selectedApartmentDetails = useSelector(
        (state: RootState) => state.bookingCreation.selectedApartmentDetails,
    );

    const personalDataValidation: PersonalDataValidation = useSelector(
        (state: RootState) => state.bookingCreation.personalDataValidation,
    );

    const selectedAdditionalServices: AdditionalService[] = useSelector(
        (state: RootState) => state.bookingCreation.selectedAdditionalServices,
    );

    const confirmEnabled = useSelector(
        (state: RootState) => state.bookingCreation.confirmEnabled,
    );

    const additionalServices: AdditionalServiceType[] =
        selectedApartmentDetails?.assetAdditionalServices ?? [];

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    }; 

    const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
        if (start) {
            dispatch(setCheckInDate({ checkInDate: start.startOf('day') }));
            dispatch(clearCheckOutDate());
        }
        if (end) {
            dispatch(setCheckOutDate({ checkOutDate: end }));
        }
    };

    const generateAvailableDates = (minDate?: Dayjs | null) => {
        // Start 1 year before today if minDate is not provided
        const start = minDate || dayjs().subtract(1, 'year').startOf('day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const generateAvailableCheckOutDates = (checkInDate: Dayjs | null) => {
        if (!checkInDate) return generateAvailableDates(dayjs());
        // Require at least 2 days after check-in
        const start = checkInDate?.add(2, 'day');
        const end = dayjs()?.add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date?.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const availableCheckInDates = generateAvailableDates(dayjs());
    const availableCheckOutDates = generateAvailableCheckOutDates(startDate);

    const handleBookingFor = (selected: string) => {
        setBookingFor(selected);
        if (selected === 'customer') {
            dispatch(setForSomeoneElse({ isForSomeoneElse: false }));
        } else {
            dispatch(setForSomeoneElse({ isForSomeoneElse: true }));
        }
    };

    const { data: locations } = useGetLocationListQuery('apartment');

    // Guests may not exceed the selected object's capacity. Enforced by the
    // API (ADR-0020); shown here so the operator sees it while typing.
    const {
        capacity: objectCapacity,
        exceeds: guestsExceedCapacity,
        blocksSave: guestsBlockSave,
    } = useGuestCapacityGuard({
        selectedObject: selectedApartment,
        selectedObjectDetails: selectedApartmentDetails,
        guests: numberOfGuests,
    });

    const saveOnlyButtonDisabled = isCreateBookingValid(
        {
            orderData: orderData,
            apartment: selectedApartment,
            personalData: personalDetails,
            someoneElseData: someoneElseDetails,
            isForSomeoneElse: isForSomeoneElse,
        },
        ['firstName', 'lastName'],
    ) && !guestsBlockSave;

    useEffect(() => {
        dispatch(clearBookingCreationStates());
        dispatch(setApartments([]));
    }, []);

    useEffect(() => {
        if (locations && locations.length > 0) {
            setCities(
                Array.from(
                    new Set(locations.map((loc: any) => loc.address.city)),
                ),
            );
            setLoadingCities(false);
        }
    }, [locations]);

    // Picked stay dates are station midnight (ADR-0019), in the search as in
    // the booking — not midnight in the operator's browser zone. The search
    // runs before an apartment (and so a location) is picked, across every
    // house in the city, so it runs on the zone those houses share (#3219);
    // the booking itself is built on the picked apartment's clock, and where
    // that differs the pick is re-checked on it below.
    const searchTimeZone = useCityTimeZone(city);
    const fetchApartments = useFetchApartments(
        startDate ? stationDateTime(startDate, undefined, searchTimeZone) : null,
        endDate ? stationDateTime(endDate, undefined, searchTimeZone) : null,
        city,
        numberOfGuests,
        undefined, // orderId
        'apartment', // useCase
    );

    useEffect(() => {
        const getApartments = async () => {
            if (city && startDate && endDate && numberOfGuests && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({includeCurrentApartment: true});
                setLoadingApartments(false);
            }
        };
        getApartments();
    }, [city, startDate, endDate, numberOfGuests, searchTimeZone]);

    // #3219: the search ran on the city's clock; the booking is built on the
    // picked apartment's. Where they differ, the pick must also be free on its
    // own clock — the server validates there and would refuse it otherwise.
    const pickedAvailability = usePickedApartmentAvailability({
        city,
        checkIn: startDate,
        checkOut: endDate,
        guests: numberOfGuests,
        useCase: 'apartment',
        pickedAssetId: selectedApartment?.assetId,
        pickedZone: timeZone,
        searchZone: searchTimeZone,
    });
    const pickedAvailabilityBlocksSave =
        pickedAvailability !== 'not_needed' && pickedAvailability !== 'available';

    const currentLanguage = i18n.language;

    useEffect(() => {
        if (apartments) {
            setApartmentNames(sortByLocaleName(apartments.map((apart) => {
                if (apart?.langNames?.[currentLanguage]?.title) {
                    return apart?.langNames?.[currentLanguage]?.title;
                } else {
                    return apart?.name;
                }
            }), (name) => name));
        }
    }, [apartments, currentLanguage]);


    useEffect(() => {
        const getApartmentDetails = async () => {
            dispatch(setSelectedApartmentDetails(null));
            if (selectedApartment) {
                await fetchApartmentDetails();
            }
        };
        getApartmentDetails();
    }, [selectedApartment]);

    useEffect(() => {
        if (countryOptions.length > 0) {
            setLoadingCountries(false);
        }
    }, [countryOptions]);

    const fetchOrders = async () => {
        await fetchBooking();
    };

    const handleCreateBooking = (saveMode: 'save_only' | 'save_and_send_email') => {
        const createBooking = async (saveMode: 'save_only' | 'save_and_send_email') => {
            if (saveMode === 'save_only') {
                setLoadingCreateBookingOnlySave(true);
            } else {
                setLoadingCreateBookingSaveAndSend(true);
            }
            const createRoomRentalBookingPayload: CreateRoomRentalBookingPayload =
                {
                    checkIn: startDate ? stationDateTime(startDate, undefined, timeZone) : new Dayjs(),
                    checkOut: endDate ? stationDateTime(endDate, undefined, timeZone) : new Dayjs(),
                    city: city ?? '',
                    numberOfGuests: numberOfGuests,
                    apartmentId: selectedApartment?.assetId ?? '',
                    // #1808: null means "no override" — the backend resolves
                    // account -> organization -> the tenant's system default.
                    customerClassId: customerClassId,
                    ...attributionPayload(attribution),
                    locationId: selectedApartment?.locationId ?? '',
                    additionalServices: selectedAdditionalServices,
                    customerDetails: {
                        firstName: personalDetails.firstName,
                        lastName: personalDetails.lastName,
                        email: personalDetails.email,
                        phone: personalDetails.phone,
                        address: personalDetails.address,
                        country: personalDetails.country,
                        city: personalDetails.city,
                        zipCode: personalDetails.zipCode,
                        company: personalDetails.company,
                    },
                    someoneElseDetails:
                        bookingFor === 'customer'
                            ? {}
                            : {
                                firstName: someoneElseDetails.firstName,
                                lastName: someoneElseDetails.lastName,
                                email: someoneElseDetails.email,
                                phone: someoneElseDetails.phone,
                            },
                    useCase: 'apartment',
                    discount: discount,
                    // #1942: an adjustment is persisted with its reason and the
                    // admin who applied it, so it is as traceable as one made
                    // on the edit screen. Empty without an adjustment.
                    ...priceAdjustmentAudit,
                    comment: comment,
                    emailNote: emailNote,
                    // BE-derived only (#1203): fully discounted ⇔ the breakdown total is 0.
                    isFullyDiscounted: breakdown?.total === 0,
                };

            const { success, mailBlocked } = await createRoomRentalBooking(
                createRoomRentalBookingPayload,
                saveMode,
            );
            if (success) {
                setSnackbarData({
                    open: true,
                    // Saved — but a refused mail is said, never shown as a plain success.
                    ...savedOutcomeToast(t, mailBlocked, t('create_booking.response.success')),
                });
                setTimeout(() => {
                    fetchOrders();
                    navigate(paths.rentalBooking);
                }, 1500);
                dispatch(clearBookingCreationStates());
                // The customer fields just cleared live in the store; the
                // organisation, unit and cost centre are this page's own state.
                setAttribution(EMPTY_BOOKING_ATTRIBUTION);
                setAttributionFormKey((k) => k + 1);
                if (saveMode === 'save_only') {
                    setLoadingCreateBookingOnlySave(false);
                } else {
                    setLoadingCreateBookingSaveAndSend(false);
                }
            } else {
                setSnackbarData({
                    open: true,
                    message: t('create_booking.response.failure'),
                    severity: 'error',
                });
                if (saveMode === 'save_only') {
                    setLoadingCreateBookingOnlySave(false);
                } else {
                    setLoadingCreateBookingSaveAndSend(false);
                }
            }
        };
        createBooking(saveMode);
    };

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (!loadingCities && cities?.length === 1 && !city) {
            dispatch(setCity({ city: cities[0] }));
        }
    }, [cities, city, loadingCities, dispatch]);

    // Booking price single source of truth (#1203): the price box renders the
    // backend breakdown from POST /admin/order/price-preview. No FE total math.
    const previewArgs: PricePreviewArgs | undefined = useMemo(() => {
        const assetId = selectedApartment?.assetId;
        if (!assetId || !startDate || !endDate) return undefined;
        return {
            useCase: 'apartment',
            assetId,
            // Calendar dates, not instants — `.toISOString()` shifts local
            // midnight to the previous UTC day and skews the night count when
            // one endpoint still carries a time component.
            checkIn: startDate.format('YYYY-MM-DD'),
            checkOut: endDate.format('YYYY-MM-DD'),
            services: selectedAdditionalServices.map((s) => ({
                serviceId: s.serviceId,
                quantity: s.quantity ?? 1,
            })),
            discount,
            amountPaid: 0,
            // #2980 D2: the class this booking will be priced by.
            customerClassId,
            // #1810: the attributed organisation (unit) is part of the class
            // chain the create prices by.
            organizationId: attribution.organizationId,
            organizationUnitId: attribution.organizationUnitId,
        };
    }, [
        selectedApartment?.assetId,
        startDate,
        endDate,
        selectedAdditionalServices,
        discount,
        customerClassId,
        attribution.organizationId,
        attribution.organizationUnitId,
    ]);

    const { breakdown, previewStatus } = usePricePreview(previewArgs);
    // PO, 2026-10-30: the backend says whether this booking's customer mail
    // may go out; "save" stays available either way.
    const mailRefusal = customerMailRefusalOf(breakdown?.customerMail);

    // Guard the save button against a negative billed total (BE-derived).
    const isTotalNegative = (breakdown?.total ?? 0) < 0;

    return (
        <Container maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Typography variant={'h5'}>
                        {t('create_booking.title')}
                    </Typography>
                </Stack>
                <Paper sx={styles.paper}>
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.reservation_details.title')}
                    </Typography>
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            display: 'flex',
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}
                        >
                            <ReservationSummary
                                isCreateBooking={true}
                                isEditBooking={false}
                                breakdown={breakdown}
                                previewStatus={previewStatus}
                                couponApplied={discount}
                                onDiscountChange={(newDiscount) => setDiscount(newDiscount)}
                                amountPaid={0}
                            />
                            <PriceAdjustmentReasonField
                                discount={discount}
                                value={priceAdjustmentReason}
                                onChange={setPriceAdjustmentReason}
                            />
                        </Grid>
                        <Grid
                            container
                            spacing={2}
                            className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}
                        >
                            <Grid item xs={12} sm={6}>
                                <DateRangeField
                                    from={startDate ? dayjs(startDate) : null}
                                    to={endDate ? dayjs(endDate) : null}
                                    onChange={(from, to) => handleDateChange(from, to)}
                                    disablePast
                                    allowedStartDays={availableCheckInDates}
                                    allowedEndDays={availableCheckOutDates}
                                    maxDate={dayjs().add(18, 'month').endOf('month')}
                                    label={
                                        t(
                                            'create_booking.reservation_details.booking_period',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={cities ?? ['']}
                                    value={city ?? ''}
                                    onChange={(value) =>
                                        dispatch(setCity({ city: value }))
                                    }
                                    disabled={cities.length === 1}
                                    loading={loadingCities}
                                    label={
                                        t(
                                            'create_booking.reservation_details.city',
                                        ) + ' *'
                                    }
                                    noOptions={t(
                                        'create_booking.reservation_details.no_apartments',
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    type={'number'}
                                    min={1}
                                    integerOnly
                                    onChange={(value) =>
                                        dispatch(
                                            setNumberOfGuests({
                                                numberOfGuests: Number(value),
                                            }),
                                        )
                                    }
                                    value={String(numberOfGuests ?? '')}
                                    error={guestsExceedCapacity}
                                    errorText={t(
                                        'create_booking.reservation_details.guests_exceed_object_capacity',
                                        { capacity: objectCapacity ?? 0 },
                                    )}
                                    label={
                                        t(
                                            'create_booking.reservation_details.number_of_guests',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {/* Absent unless the tenant has more than the
                                    seeded default class. */}
                                <BookingCustomerClassSelect
                                    value={customerClassId}
                                    inheritedClassId={inheritedClassId}
                                    onChange={setCustomerClassId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <SelectField
                                    options={apartmentNames ?? []}
                                    value={selectedApartment?.name ?? ''}
                                    onChange={(apart) => {
                                        if (apartments) {
                                            const selected = apartments.find(
                                                (apartment) =>
                                                    apartment.name === apart,
                                            );
                                            if (selected) {
                                                dispatch(
                                                    setSelectedApartment(selected),
                                                );
                                            }
                                        }
                                    }}
                                    loading={loadingApartments}
                                    error={pickedAvailability === 'unavailable' || pickedAvailability === 'failed'}
                                    label={
                                        t(
                                            'create_booking.reservation_details.apartment',
                                        ) + ' *'
                                    }
                                    noOptions={t(
                                        'create_booking.reservation_details.no_apartments_match',
                                    )}
                                />
                                {
                                    selectedApartment &&
                                    <Typography variant={'body1'} fontWeight={'bold'} sx={{ mt: 2 }}>
                                        {/* eslint-disable-next-line max-len */}
                                        {`${extractAddress(selectedApartment?.name)}, ${selectedApartment?.street}, ${selectedApartment?.postalCode} ${selectedApartment?.city}`}
                                    </Typography>
                                }
                                {pickedAvailability === 'checking' && (
                                    <p className={'mt-1 text-xs text-foreground/60'}>
                                        {t('create_booking.reservation_details.apartment_zone_check_running')}
                                    </p>
                                )}
                                {pickedAvailability === 'unavailable' && (
                                    <p className={'mt-1 text-xs text-error'}>
                                        {t('create_booking.reservation_details.apartment_unavailable_in_its_zone')}
                                    </p>
                                )}
                                {pickedAvailability === 'failed' && (
                                    <p className={'mt-1 text-xs text-error'}>
                                        {t('create_booking.reservation_details.apartment_zone_check_failed')}
                                    </p>
                                )}
                                {!apartments && (
                                    <p className={'text-sm text-gray-500 mt-1'}>
                                        {t(
                                            'create_booking.reservation_details.apartment_note',
                                        )}
                                    </p>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-2'}>
                        {t('create_booking.additional_services.title')}
                    </Typography>
                    {selectedApartment ? (
                        selectedApartmentDetails ? (
                            <AdditionalServices
                                additionalServices={additionalServices}
                            />
                        ) : (
                            <div
                                className={
                                    'flex items-center space-x-2 text-gray-500'
                                }
                            >
                                <CircularProgress
                                    className={'text-gray-500'}
                                    size={20}
                                />
                                <Typography className={'text-gray-500'}>
                                    {t('common.loading')}
                                </Typography>
                            </div>
                        )
                    ) : (
                        <Typography>
                            {t('create_booking.additional_services.note')}
                        </Typography>
                    )}
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.customer_details.title')}
                    </Typography>
                    <Grid
                        container={true}
                        spacing={2}
                        className={'w-[100%] xl:w-[70%]'}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onFirstNameChange}
                                value={personalDetails.firstName}
                                label={
                                    t(
                                        'create_booking.customer_details.first_name',
                                    ) + ' *'
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onLastNameChange}
                                value={personalDetails.lastName}
                                label={
                                    t(
                                        'create_booking.customer_details.last_name',
                                    ) + ' *'
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onEmailChange}
                                value={personalDetails.email}
                                label={
                                    t('create_booking.customer_details.email')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <PhoneNumberComponent
                                initialValue={personalDetails.phone}
                                onChange={onPhoneChange}
                                valid={personalDataValidation.phone}
                                required={false}
                                label={
                                    t('create_booking.customer_details.phone')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onCompanyNameChange}
                                value={personalDetails.company}
                                label={
                                    t(
                                        'create_booking.customer_details.company',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <BookingAttributionFields
                                key={attributionFormKey}
                                value={attribution}
                                onChange={setAttribution}
                                onOrganizationPicked={(prefill) => applyContactPrefill(prefill, {
                                    company: onCompanyNameChange,
                                    address: onAddressChange,
                                    zipCode: onZipCodeChange,
                                    city: onCityChange,
                                    country: onCountryChange,
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onAddressChange}
                                value={personalDetails.address}
                                label={
                                    t(
                                        'create_booking.customer_details.address',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FilteredSelect
                                options={countryOptions}
                                selectedOption={toCountryLabel(personalDetails.country ?? '')}
                                handleOptionChange={(label) => onCountryChange(toCountryValue(label))}
                                label={
                                    t(
                                        'create_booking.customer_details.country',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onCityChange}
                                value={personalDetails.city}
                                label={
                                    t('create_booking.customer_details.city')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onZipCodeChange}
                                value={personalDetails.zipCode}
                                label={
                                    t(
                                        'create_booking.customer_details.zip_code',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onCommentChange}
                                value={comment}
                                label={
                                    t(
                                        'create_booking.customer_details.comment',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onEmailNoteChange}
                                value={emailNote}
                                maxLength={BOOKING_EMAIL_NOTE_MAX_LENGTH}
                                label={
                                    t(
                                        'create_booking.customer_details.email_note',
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.booking_for.title')}
                    </Typography>
                    <RadioGroup
                        row
                        value={bookingFor}
                        onChange={(e) => handleBookingFor(e.target.value)}
                        sx={{ paddingLeft: '13px', marginBottom: '14px' }}
                    >
                        <FormControlLabel
                            value={'customer'}
                            control={
                                <Radio
                                    sx={{
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.light,
                                        },
                                    }}
                                />
                            }
                            label={t('create_booking.booking_for.customer')}
                        />
                        <FormControlLabel
                            value={'someone_else'}
                            control={
                                <Radio
                                    sx={{
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.light,
                                        },
                                    }}
                                />
                            }
                            label={t('create_booking.booking_for.someone_else')}
                        />
                    </RadioGroup>

                    {bookingFor === 'someone_else' && (
                        <Box>
                            <Typography variant={'h3'} className={'mb-3 mt-5'}>
                                {t('create_booking.booking_for.sub_title')}
                            </Typography>
                            <Grid
                                container={true}
                                spacing={2}
                                className={'w-[100%] xl:w-[70%]'}
                                sx={{
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseFirstNameChange}
                                        value={someoneElseDetails.firstName}
                                        label={
                                            t(
                                                'create_booking.booking_for.first_name',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseLastNameChange}
                                        value={someoneElseDetails.lastName}
                                        label={
                                            t(
                                                'create_booking.booking_for.last_name',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseEmailChange}
                                        value={someoneElseDetails.email}
                                        label={
                                            t(
                                                'create_booking.booking_for.email',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneNumberComponent
                                        initialValue={someoneElseDetails.phone}
                                        onChange={onSomeoneElsePhoneChange}
                                        valid={
                                            someoneElseDetails.phone.length > 1
                                        }
                                        required={false}
                                        label={
                                            t(
                                                'create_booking.booking_for.phone',
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    <div className={'flex space-x-2 mt-6'}>
                        <CustomButton
                            title={t(
                                'create_booking.button.cancel',
                            )}
                            onClick={() => navigate(paths.rentalBooking)}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t(
                                'create_booking.button.confirm_only',
                            )}
                            onClick={() => handleCreateBooking('save_only')}
                            loading={loadingCreateBookingOnlySave}
                            disabled={
                                loadingCreateBookingSaveAndSend
                                || loadingCreateBookingOnlySave
                                || !saveOnlyButtonDisabled
                                || isTotalNegative
                                || pickedAvailabilityBlocksSave
                            }
                            isFilled={false}
                        />
                        <CustomButton
                            title={t(
                                'create_booking.button.confirm_and_send',
                            )}
                            onClick={() => setOpen(true)}
                            isFilled={true}
                            // loading={loadingCreateBookingSaveAndSend}
                            disabled={
                                loadingCreateBookingSaveAndSend 
                                || loadingCreateBookingOnlySave 
                                || !confirmEnabled
                                || isTotalNegative
                                || pickedAvailabilityBlocksSave
                                || !personalDataValidation.email
                                || !!mailRefusal
                            }
                        />
                    </div>
                    <CustomerMailBlockedNote refusal={mailRefusal} />
                    <SnackbarComponent
                        open={snackbarData.open}
                        message={snackbarData.message}
                        severity={snackbarData.severity}
                        onClose={handleCloseSnackbar}
                    />
                    <CustomModal
                        open={open}
                        onClose={() => setOpen(false)}
                        title={t('create_booking.modal.title')}
                        saveButton={
                            <CustomButton
                                title={t('create_booking.modal.confirm_button')}
                                onClick={() => handleCreateBooking('save_and_send_email')}
                                disabled={loadingCreateBookingSaveAndSend || !personalDataValidation.email || !!mailRefusal}
                                loading={loadingCreateBookingSaveAndSend}
                                isFilled={true}
                                fullWidth={false}
                            />
                        }
                        closeButton={
                            <CustomButton
                                title={t(
                                    'create_booking.modal.cancel_button',
                                )}
                                onClick={() => setOpen(false)}
                                isFilled={false}
                            />
                        }
                        description={t('create_booking.modal.description')}
                    >
                    </CustomModal>
                </Paper>
            </Box>
        </Container>
    );
};

export default CreateRoomBookingPage;
